import { Link } from "react-router-dom";
import {
  PhotoIcon,
  ShieldCheckIcon,
  MegaphoneIcon,
  QrCodeIcon,
  UserIcon,
  SwatchIcon,
  TrashIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import type { ComponentType } from "react";
import { usePermissions } from "../../contexts/PermissionContext";
import { useFeatureFlag } from "../../contexts/FeatureFlagContext";
import { FeatureFlag } from "../../constants/featureFlags";
import AnalyticsIcon from "../../assets/icons/analytics.svg";
import ContentManager from "../../assets/icons/content_icon.svg";
import Container from "../../componentLibrary/Container";

interface AdminCard {
  title: string;
  description: string;
  link: string;
  icon: ComponentType<{ className?: string }> | string;
  iconAlt: string;
  isHeroIcon?: boolean;
}

interface AdminGroup {
  title: string;
  cards: AdminCard[];
}

const AdminPage = () => {
  const { hasPermission, isOwner } = usePermissions();
  const contentDeletionEnabled = useFeatureFlag(FeatureFlag.CONTENT_DELETION);
  const newToolboxesEnabled = useFeatureFlag(FeatureFlag.NEW_TOOLBOXES);

  // Check specific permissions
  const canViewAnalytics = hasPermission("analytics:read");
  const canManageContent = hasPermission([
    "content:create",
    "content:read",
    "content:update",
  ]);
  const canViewDraftTopics = hasPermission("content:read");
  const canManageAccessCodes = hasPermission("accessCode:read");
  const canManageImages = hasPermission([
    "content:*",
    "content:read",
    "content:create",
    "content:update",
  ]);
  const canManageAnnouncements = hasPermission("announcement:*");
  const canDeleteContent =
    hasPermission("content:delete") && hasPermission("content:read");

  const adminGroups: AdminGroup[] = [];

  // Content Management Group
  const contentManagementCards: AdminCard[] = [];
  if (canManageContent) {
    contentManagementCards.push({
      title: "Content Manager",
      description: "Manage modules, lessons, and content",
      link: "/admin/content-management",
      icon: ContentManager,
      iconAlt: "Content Manager",
    });
  }
  if (canViewDraftTopics) {
    contentManagementCards.push({
      title: "Draft Topics",
      description: "View and manage draft topics",
      link: "/admin/draft-topics",
      icon: ContentManager,
      iconAlt: "Draft Topics",
    });
    contentManagementCards.push({
      title: "Component Library",
      description: "View UI components and design system",
      link: "/component-library",
      icon: SwatchIcon,
      iconAlt: "Component Library",
      isHeroIcon: true,
    });
  }
  if (canManageImages) {
    contentManagementCards.push({
      title: "Image Management",
      description: "Upload and manage images",
      link: "/admin/image-management",
      icon: PhotoIcon,
      iconAlt: "Image Management",
      isHeroIcon: true,
    });
  }
  if (canDeleteContent && contentDeletionEnabled) {
    contentManagementCards.push({
      title: "Content Delete",
      description: "Delete unlinked lessons and pages",
      link: "/admin/content-delete",
      icon: TrashIcon,
      iconAlt: "Content Delete",
      isHeroIcon: true,
    });
  }
  if (newToolboxesEnabled && canManageContent) {
    contentManagementCards.push({
      title: "Toolbox Manager",
      description: "Customize topic toolbox overview pages",
      link: "/admin/toolboxes",
      icon: WrenchScrewdriverIcon,
      iconAlt: "Toolbox Manager",
      isHeroIcon: true,
    });
  }
  if (contentManagementCards.length > 0) {
    adminGroups.push({
      title: "Content Management",
      cards: contentManagementCards,
    });
  }

  // Analytics Group
  const analyticsCards: AdminCard[] = [];
  if (canViewAnalytics) {
    analyticsCards.push({
      title: "Analytics Dashboard",
      description: "View analytics and insights",
      link: "/admin/analytics-dashboard",
      icon: AnalyticsIcon,
      iconAlt: "Analytics",
    });
  }
  if (analyticsCards.length > 0) {
    adminGroups.push({
      title: "Analytics",
      cards: analyticsCards,
    });
  }

  // Access Management Group
  const accessManagementCards: AdminCard[] = [];
  if (canManageAccessCodes) {
    accessManagementCards.push({
      title: "Access Code",
      description: "Manage access codes",
      link: "/admin/access-codes",
      icon: QrCodeIcon,
      iconAlt: "Access Code",
      isHeroIcon: true,
    });
  }
  if (accessManagementCards.length > 0) {
    adminGroups.push({
      title: "Access Management",
      cards: accessManagementCards,
    });
  }

  // Communication Group
  const communicationCards: AdminCard[] = [];
  if (canManageAnnouncements) {
    communicationCards.push({
      title: "Announcement Manager",
      description: "Create and manage announcements",
      link: "/admin/announcement-manager",
      icon: MegaphoneIcon,
      iconAlt: "Announcement Manager",
      isHeroIcon: true,
    });
  }
  if (communicationCards.length > 0) {
    adminGroups.push({
      title: "Communication",
      cards: communicationCards,
    });
  }

  // System Administration Group
  const systemAdminCards: AdminCard[] = [];
  if (isOwner) {
    systemAdminCards.push({
      title: "User Role Management",
      description: "Manage user roles and assignments",
      link: "/admin/user-role-management",
      icon: UserIcon,
      iconAlt: "User Role Management",
      isHeroIcon: true,
    });
    systemAdminCards.push({
      title: "Role & Permission Management",
      description: "Manage roles and permissions",
      link: "/admin/role-permission-management",
      icon: ShieldCheckIcon,
      iconAlt: "Role & Permission Management",
      isHeroIcon: true,
    });
  }
  if (systemAdminCards.length > 0) {
    adminGroups.push({
      title: "System Administration",
      cards: systemAdminCards,
    });
  }

  return (
    <div className="p-2 sm:p-4">
      <Container>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Admin Dashboard
          </h1>
          <p className="text-gray-600">
            Manage your platform settings and content
          </p>
        </div>

        <div className="space-y-8">
          {adminGroups.map((group) => (
            <div key={group.title} className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-700 border-b border-gray-300 pb-2">
                {group.title}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {group.cards.map((card) => {
                  const IconComponent =
                    card.isHeroIcon && typeof card.icon !== "string"
                      ? (card.icon as ComponentType<{ className?: string }>)
                      : null;
                  const cardContent = (
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        {IconComponent ? (
                          <IconComponent className="w-8 h-8 text-blue-600" />
                        ) : (
                          <img
                            src={typeof card.icon === "string" ? card.icon : ""}
                            alt={card.iconAlt}
                            className="w-8 h-8"
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-800 mb-1">
                          {card.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {card.description}
                        </p>
                      </div>
                    </div>
                  );
                  const cardClassName =
                    "block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 p-6 border border-gray-200 hover:border-blue-500";
                  return (
                    <Link
                      key={card.link}
                      to={card.link}
                      className={cardClassName}
                      aria-label={card.title}
                    >
                      {cardContent}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Container>
    </div>
  );
};

export default AdminPage;
